import React, { useState, useEffect } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  Row,
  Col,
  PreviewAltCard,
  Icon,
  Button,
  RSelect,
} from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import { Spinner, Badge } from "reactstrap";
import IntelligentReportBlock from "../../../components/reports/IntelligentReportBlock";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const SiteReportsPage = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [startDate, setStartDate] = useState(new Date(2026, 3, 1));
  const [endDate, setEndDate] = useState(new Date());

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    if (selectedSiteId) {
      loadReport();
    }
  }, [selectedSiteId, startDate, endDate]);

  const loadSites = async () => {
    try {
      const response = await api.getSites();
      const sitesList = Array.isArray(response) ? response : response.data || [];
      setSites(sitesList);
      if (sitesList.length > 0 && !selectedSiteId) {
        setSelectedSiteId(sitesList[0].id);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des sites:", error);
    }
  };

  const loadReport = async () => {
    if (!selectedSiteId) return;
    setLoading(true);
    try {
      const data = await api.getSiteReport({
        site_id: selectedSiteId,
        start_date: moment(startDate).format("YYYY-MM-DD"),
        end_date: moment(endDate).format("YYYY-MM-DD"),
      });
      setReportData(data);
    } catch (error) {
      toast.error("Erreur lors du chargement du rapport");
      console.error("Error loading report:", error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const siteOptions = sites.map((site) => ({ value: site.id, label: site.name }));

  const getTypeLabel = (type) => {
    const labels = {
      positive: "Positive",
      dangerous: "Dangereuse",
      near_miss: "Presque accident",
      non_conformity: "Non-conformité",
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: "Brouillon",
      submitted: "Soumis",
      received: "Reçue",
      in_analysis: "En analyse",
      corrective_action_requested: "Action requise",
      action_completed: "Action réalisée",
      verified: "Vérifiée",
      in_action: "En action",
      closed: "Clôturé",
      rejected: "Rejeté",
    };
    return labels[status] || status;
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      low: "Faible",
      medium: "Moyenne",
      high: "Élevée",
    };
    return labels[severity] || severity;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: "success",
      medium: "warning",
      high: "danger",
    };
    return colors[severity] || "secondary";
  };

  return (
    <React.Fragment>
      <Head title="Rapports par Site" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Rapports par Site</BlockTitle>
              <BlockDes className="text-soft">
                <p>Générez et consultez les rapports HSE par site</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
              </ul>
            </BlockHeadContent>
          </BlockBetween>
          <div className="mt-3">
            <Row className="g-3">
              <Col md="4">
                <div className="form-control-wrap">
                  <label className="form-label">Site</label>
                  <RSelect
                    options={siteOptions}
                    value={siteOptions.find((opt) => opt.value === selectedSiteId) || null}
                    onChange={(option) => setSelectedSiteId(option ? option.value : null)}
                    placeholder="Site Principal"
                  />
                </div>
              </Col>
              <Col md="4">
                <div className="form-control-wrap">
                  <label className="form-label">Date de début</label>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    placeholderText="01/04/2026"
                  />
                </div>
              </Col>
              <Col md="4">
                <div className="form-control-wrap">
                  <label className="form-label">Date de fin</label>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    placeholderText="Date de fin"
                  />
                </div>
              </Col>
            </Row>
          </div>
        </BlockHead>

        {loading ? (
          <Block>
            <div className="text-center p-4">
              <Spinner color="primary" />
              <p className="mt-2 text-soft">Chargement du rapport...</p>
            </div>
          </Block>
        ) : reportData ? (
          <>
            {/* Vue d'ensemble */}
            <Block>
              <Row className="g-gs">
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Total Observations</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{reportData.overview.total}</span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">
                          {reportData.overview.positive} positives • {reportData.overview.negative}{" "}
                          négatives
                        </span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Taux de clôture</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{reportData.overview.closure_rate}%</span>
                      </div>
                      <div className="card-stats">
                        <span className="text-soft">{reportData.overview.closed} clôturées</span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Positives</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{reportData.overview.positive}</span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col md="6" lg="3">
                  <PreviewAltCard className="card-full">
                    <div className="card-inner">
                      <div className="card-title-group">
                        <div className="card-title">
                          <h6 className="title">Négatives</h6>
                        </div>
                      </div>
                      <div className="card-amount">
                        <span className="amount">{reportData.overview.negative}</span>
                      </div>
                    </div>
                  </PreviewAltCard>
                </Col>
              </Row>
            </Block>

            {/* Répartition par type */}
            <Block>
              <Row className="g-gs">
                <Col lg="6">
                  <PreviewAltCard>
                    <div className="nk-block-head">
                      <h6 className="title">Répartition par type</h6>
                    </div>
                    <div className="nk-tb-list nk-tb-ulog">
                      <div className="nk-tb-item nk-tb-head">
                        <div className="nk-tb-col">
                          <span>Type</span>
                        </div>
                        <div className="nk-tb-col tb-col-sm">
                          <span>Nombre</span>
                        </div>
                        <div className="nk-tb-col tb-col-sm">
                          <span>Pourcentage</span>
                        </div>
                      </div>
                      {reportData.by_type.map((item, index) => (
                        <div className="nk-tb-item" key={index}>
                          <div className="nk-tb-col">
                            <span>{getTypeLabel(item.type)}</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>{item.count}</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>
                              {reportData.overview.total > 0
                                ? ((item.count / reportData.overview.total) * 100).toFixed(1)
                                : 0}
                              %
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </PreviewAltCard>
                </Col>
                <Col lg="6">
                  <PreviewAltCard>
                    <div className="nk-block-head">
                      <h6 className="title">Répartition par statut</h6>
                    </div>
                    <div className="nk-tb-list nk-tb-ulog">
                      <div className="nk-tb-item nk-tb-head">
                        <div className="nk-tb-col">
                          <span>Statut</span>
                        </div>
                        <div className="nk-tb-col tb-col-sm">
                          <span>Nombre</span>
                        </div>
                        <div className="nk-tb-col tb-col-sm">
                          <span>Pourcentage</span>
                        </div>
                      </div>
                      {reportData.by_status.map((item, index) => (
                        <div className="nk-tb-item" key={index}>
                          <div className="nk-tb-col">
                            <span>{getStatusLabel(item.status)}</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>{item.count}</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>
                              {reportData.overview.total > 0
                                ? ((item.count / reportData.overview.total) * 100).toFixed(1)
                                : 0}
                              %
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </PreviewAltCard>
                </Col>
              </Row>
            </Block>

            {/* Répartition par gravité */}
            {reportData.by_severity && reportData.by_severity.length > 0 && (
              <Block>
                <Row className="g-gs">
                  <Col lg="6">
                    <PreviewAltCard>
                      <div className="nk-block-head">
                        <h6 className="title">Répartition par gravité</h6>
                      </div>
                      <div className="nk-tb-list nk-tb-ulog">
                        <div className="nk-tb-item nk-tb-head">
                          <div className="nk-tb-col">
                            <span>Gravité</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Nombre</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Pourcentage</span>
                          </div>
                        </div>
                        {reportData.by_severity.map((item, index) => (
                          <div className="nk-tb-item" key={index}>
                            <div className="nk-tb-col">
                              <Badge color={getSeverityColor(item.severity)}>
                                {getSeverityLabel(item.severity)}
                              </Badge>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>{item.count}</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>
                                {reportData.overview.total > 0
                                  ? ((item.count / reportData.overview.total) * 100).toFixed(1)
                                  : 0}
                                %
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </PreviewAltCard>
                  </Col>
                </Row>
              </Block>
            )}

            {/* Top créateurs */}
            {reportData.top_creators && reportData.top_creators.length > 0 && (
              <Block>
                <Row className="g-gs">
                  <Col lg="6">
                    <PreviewAltCard>
                      <div className="nk-block-head">
                        <h6 className="title">Top créateurs d'observations</h6>
                      </div>
                      <div className="nk-tb-list nk-tb-ulog">
                        <div className="nk-tb-item nk-tb-head">
                          <div className="nk-tb-col">
                            <span>Rang</span>
                          </div>
                          <div className="nk-tb-col">
                            <span>Créateur</span>
                          </div>
                          <div className="nk-tb-col tb-col-sm">
                            <span>Nombre</span>
                          </div>
                        </div>
                        {reportData.top_creators.map((item, index) => (
                          <div className="nk-tb-item" key={index}>
                            <div className="nk-tb-col">
                              <span>#{index + 1}</span>
                            </div>
                            <div className="nk-tb-col">
                              <span>
                                {item.creator
                                  ? `${item.creator.first_name} ${item.creator.last_name}`
                                  : "N/A"}
                              </span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>{item.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </PreviewAltCard>
                  </Col>
                  {reportData.top_risks && reportData.top_risks.length > 0 && (
                    <Col lg="6">
                      <PreviewAltCard>
                        <div className="nk-block-head">
                          <h6 className="title">Top types de risques</h6>
                        </div>
                        <div className="nk-tb-list nk-tb-ulog">
                          <div className="nk-tb-item nk-tb-head">
                            <div className="nk-tb-col">
                              <span>Rang</span>
                            </div>
                            <div className="nk-tb-col">
                              <span>Type de risque</span>
                            </div>
                            <div className="nk-tb-col tb-col-sm">
                              <span>Nombre</span>
                            </div>
                          </div>
                          {reportData.top_risks.map((item, index) => (
                            <div className="nk-tb-item" key={index}>
                              <div className="nk-tb-col">
                                <span>#{index + 1}</span>
                              </div>
                              <div className="nk-tb-col">
                                <span>{item.risk_potential}</span>
                              </div>
                              <div className="nk-tb-col tb-col-sm">
                                <span>{item.count}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </PreviewAltCard>
                    </Col>
                  )}
                </Row>
              </Block>
            )}
          </>
        ) : (
          <Block>
            <div className="text-center p-4">
              <span className="text-soft">
                {selectedSiteId
                  ? "Aucune donnée disponible pour cette période"
                  : "Veuillez sélectionner un site"}
              </span>
            </div>
          </Block>
        )}

        {selectedSiteId && (
          <IntelligentReportBlock
            getPayload={() => ({
              kind: "site",
              site_id: selectedSiteId,
              start_date: moment(startDate).format("YYYY-MM-DD"),
              end_date: moment(endDate).format("YYYY-MM-DD"),
            })}
            pdfMeta={{
              subtitle: `${sites.find((s) => s.id === selectedSiteId)?.name ?? ""} · ${moment(startDate).format("DD/MM/YYYY")} – ${moment(endDate).format("DD/MM/YYYY")}`,
              filename: `synthese-ia-${moment().format("YYYY-MM-DD")}`,
            }}
          />
        )}
      </Content>
    </React.Fragment>
  );
};

export default SiteReportsPage;

